import { Rules } from '@/components/ui/icons';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { errorMessage } from '@/api/client';
import { serverApi } from '@/api/endpoints';
import { MessageContent } from '@/components/chat/MessageContent';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useMyPermissions } from '@/lib/permissions';
import { useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';

/** Which server's rules are open, and which already popped up this visit (they then wait behind the banner). */
const useRulesPrompt = create<{ openFor: string | null; shown: Record<string, true>; open: (serverId: string) => void; close: () => void }>((set) => ({
  openFor: null,
  shown: {},
  open: (serverId) => set((s) => ({ openFor: serverId, shown: { ...s.shown, [serverId]: true } })),
  close: () => set({ openFor: null }),
}));

/** Whether you still have to accept this server's rules before chatting. */
export function useRulesPending(serverId: string): boolean {
  return useMyPermissions(serverId).rulesPending;
}

/**
 * Until you accept a server's rules you can read but not chat: a banner says
 * so, and the rules open by themselves the first time you visit.
 */
export function RulesBanner({ serverId }: { serverId: string }) {
  const pending = useRulesPending(serverId);
  const open = useRulesPrompt((s) => s.openFor === serverId);
  const alreadyShown = useRulesPrompt((s) => !!s.shown[serverId]);

  useEffect(() => {
    if (pending && !alreadyShown) useRulesPrompt.getState().open(serverId);
  }, [pending, alreadyShown, serverId]);

  if (!pending) return null;
  return (
    <>
      <div className="mx-3 mt-2 flex flex-wrap items-center gap-3 rounded-xl border border-accent/40 bg-accent-soft px-4 py-2.5 text-sm sm:mx-4">
        <Rules size={18} className="shrink-0 text-accent" />
        <p className="min-w-0 flex-1 text-fg">This server has rules. Read and accept them to start chatting.</p>
        <Button size="sm" onClick={() => useRulesPrompt.getState().open(serverId)}>
          Read the rules
        </Button>
      </div>
      {open && <RulesModal serverId={serverId} onClose={() => useRulesPrompt.getState().close()} />}
    </>
  );
}

function RulesModal({ serverId, onClose }: { serverId: string; onClose: () => void }) {
  const server = useServers((s) => s.servers[serverId]);
  const [busy, setBusy] = useState(false);
  if (!server) return null;

  const accept = async () => {
    setBusy(true);
    try {
      const update = await serverApi.acceptRules(serverId);
      useServers.getState().patchServer(serverId, { me: { ...server.me, rulesAcceptedAt: update.rulesAcceptedAt } });
      toast.success(`Welcome to ${server.name}!`);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${server.name} rules`}
      description="Read these before you start chatting."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Not now
          </Button>
          <Button loading={busy} onClick={() => void accept()}>
            I’ve read and agree
          </Button>
        </>
      }
    >
      <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-line bg-sidebar p-4">
        <MessageContent content={server.rules ?? ''} serverId={serverId} className="text-[15px] leading-relaxed text-fg" />
      </div>
    </Modal>
  );
}
